import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FileFilterBody, PagedData } from '../models/general.models';
import {
  Project,
  ProjectManagement,
  ProjectManagementResponse
} from '../models/project.module';
import { RiskScores } from '../models/report.model';

@Injectable({
  providedIn: 'root'
})
export class ProjectService {
  private baseUrl: string = environment.baseUrl;
  public metadataKeys: string[] = [];

  constructor(private http: HttpClient) {}

  public getProjectsByPath(
    path?: string,
    archived?: boolean
  ): Observable<Project> {
    // Handle empty or undefined path - use root projects endpoint
    // if (!path || path === '' || path === 'root') {
    //   let endpoint = `${this.baseUrl}/projects`;
    //   if (archived) {
    //     endpoint += `?is_archived=${archived}`;
    //   }
    //   return this.http.get<any>(endpoint).pipe(
    //     map((response) => {
    //       // Handle new API response format
    //       if (response.items) {
    //         // New API format - convert to old format
    //         return {
    //           id: 'root',
    //           name: 'Root',
    //           path: 'root',
    //           is_root: true,
    //           children: response.items,
    //           user: { email: '' },
    //           team: { name: '' },
    //           shared_with: [],
    //           is_archived: false,
    //           skipped_paths: [],
    //           description: '',
    //           bounding_box: null,
    //           items: []
    //         } as Project;
    //       }
    //       return response;
    //     })
    //   );
    // }

    // For specific paths, use the path endpoint
    let endpoint = `${this.baseUrl}/projects/by-path/${path}`;
    if (archived) {
      endpoint += `?is_archived=${archived}`;
    }
    return this.http.get<Project>(endpoint);
  }

  public searchProjects(search: string): Observable<Array<Project>> {
    const endpoint = `${this.baseUrl}/projects/search`;
    const searchRequest = {
      query: search || '',
      skip: 0,
      limit: 100
    };
    return this.http.post<any>(endpoint, searchRequest).pipe(
      map((response) => {
        if (response.items) {
          return response.items;
        }
        return response;
      })
    );
  }

  public getProjectsManagement(): Observable<PagedData<ProjectManagement>> {
    const endpoint = `${this.baseUrl}/projects/management`;
    // Object.keys(queryKeys).forEach((key, index) => {
    // const jointer = index === 0 ? '?' : '&';
    // endpoint += `${jointer}${key}=${queryKeys[key]}`;
    // });
    return this.http.get<PagedData<ProjectManagement>>(endpoint);
  }

  public getProjectManagementById(id: string): Observable<ProjectManagement> {
    const endpoint = `${this.baseUrl}/projects/management/${id}`;
    return this.http.get<ProjectManagement>(endpoint);
  }

  public getProjectActivities(
    id: string,
    type: string,
    filter: FileFilterBody
  ): Observable<ProjectManagementResponse> {
    let endpoint = `${this.baseUrl}/projects/management/${id}/activities/?type=${type}`;
    const jointer = '&';
    Object.keys(filter).forEach((key) => {
      if (key === 'file_format' && filter[key]?.length) {
        endpoint += `${jointer}${key}=${filter[key]}`;
      }
      if (key === 'date' && filter[key]?.from && filter[key]?.to) {
        endpoint += `${jointer}created_after=${filter[key].from.toISOString()}`;
        endpoint += `${jointer}created_before=${filter[key].to.toISOString()}`;
      }
      if (key === 'ownership' && filter[key]?.length) {
        endpoint += `${jointer}user_id=${filter[key]
          .map((user) => user.user_id)
          .join(',')}`;
      }
    });
    return this.http.get<ProjectManagementResponse>(endpoint);
  }

  public getProjectById(id: number | string): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.get<Project>(endpoint);
  }

  public changeProject(
    id: string,
    body: Partial<Project>
  ): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.patch<Project>(endpoint, { ...body });
  }

  public deleteProject(id: string): Observable<any> {
    const endpoint = `${this.baseUrl}/projects/${id}`;
    return this.http.delete(endpoint);
  }

  public archiveProject(
    project: Project | ProjectManagement
  ): Observable<any> {
    const type = !project.is_archived ? 'archive' : 'unarchive';
    const endpoint = `${this.baseUrl}/projects/${project.id}/${type}`;
    const body = {
      name: project.name
    };
    return this.http.post(endpoint, body);
  }

  public createProject(body: {
    name: string;
    parent_id?: number | string;
    description?: string;
    tenant_id?: string;
    team_id?: string;
    project_metadata?: any;
  }): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects`;
    return this.http.post<Project>(endpoint, body);
  }

  public shareProject(
    body: { shared_with_user_id: any; project_id: any; permissions: string[] }
  ): Observable<Project> {
    const endpoint = `${this.baseUrl}/shares/`;
    return this.http.post<Project>(endpoint, body);
  }

  public leaveSharedProject(id: string): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}/leave_shared_project/`;
    return this.http.post<Project>(endpoint, {});
  }

  public removeShareProject(
    body: { user_id: string | number },
    id: string
  ): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${id}/remove_share`;
    return this.http.post<Project>(endpoint, body);
  }

  public getAreaMetadataKeys(id: string): Observable<any> {
    const endpoint = `${this.baseUrl}/areas/metadata-keys?project_id=${id}`;
    return this.http.get<RiskScores>(endpoint);
  }

  public moveFolder(folderId: string, newParentId: string): Observable<Project> {
    const endpoint = `${this.baseUrl}/projects/${folderId}/move`;
    return this.http.put<Project>(endpoint, { new_parent_id: newParentId });
  }
}
